import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import useReveal from "@/hooks/useReveal";
import { useCurrency } from "@/context/CurrencyContext";
import { Check } from "@phosphor-icons/react";

const SERVICES = [
  { title: "SEO & Content", desc: "Editorial calendars, technical SEO audits and backlink outreach." },
  { title: "Paid Media", desc: "Google, Meta, LinkedIn and TikTok — with weekly optimisation." },
  { title: "Analytics", desc: "GA4, server-side tracking and revenue attribution dashboards." },
  { title: "Brand & Creative", desc: "Ad creative, landing pages and campaign identity systems." },
];

export default function Marketing() {
  const ref = useReveal();
  const { format } = useCurrency();
  const [tiers, setTiers] = useState([]);
  useEffect(() => { api.get("/marketing-tiers").then((r) => setTiers(r.data)).catch(() => {}); }, []);

  return (
    <section id="marketing" ref={ref} data-testid="marketing" className="reveal section border-b border-[color:var(--line)] bg-white">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-6">
            <div className="eyebrow">Digital marketing</div>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-4 leading-[1.02]">
              Growth retainers that tie back to pipeline.
            </h2>
          </div>
          <p className="lg:col-span-5 lg:col-start-8 text-neutral-700 text-lg leading-relaxed">
            No vanity metrics. Every retainer ships with a live dashboard mapping spend to qualified pipeline.
          </p>
        </div>

        <div className="grid md:grid-cols-4 border-l border-t border-[color:var(--line)] mb-16">
          {SERVICES.map((s, i) => (
            <div key={s.title} data-testid={`marketing-service-${i}`} className="border-r border-b border-[color:var(--line)] p-8 hover:bg-[color:var(--paper-alt)] transition-colors duration-200">
              <span className="font-mono text-[10px] text-[color:var(--brand)] uppercase tracking-widest">0{i + 1}</span>
              <h3 className="mt-3 font-display font-bold text-xl">{s.title}</h3>
              <p className="mt-2 text-neutral-600 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((t) => (
            <div
              key={t.id}
              data-testid={`marketing-tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`p-8 border transition-all ${t.highlight ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5 shadow-[0_20px_40px_-20px_rgba(30,136,240,0.35)]" : "border-[color:var(--line)] hover:border-[color:var(--ink)]"}`}
            >
              <div className="eyebrow">{t.tagline}</div>
              <h3 className="mt-3 font-display font-bold text-2xl">{t.name}</h3>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display font-black text-4xl">{format(t.price_inr_monthly)}</span>
                <span className="font-mono text-xs uppercase tracking-widest text-neutral-500">/ month</span>
              </div>
              <ul className="mt-6 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="text-sm flex gap-2 items-start text-neutral-700">
                    <Check size={16} weight="bold" className="text-[color:var(--brand)] mt-1" /> {f}
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                data-testid={`marketing-cta-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`mt-8 inline-flex justify-center w-full py-3 px-4 font-semibold text-sm transition-colors duration-200 ${
                  t.highlight ? "bg-[color:var(--brand)] text-white hover:bg-[color:var(--brand-dark)]" : "border border-[color:var(--ink)] text-[color:var(--ink)] hover:bg-[color:var(--ink)] hover:text-white"
                }`}
              >
                Book a call
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
